import { useMemo, useState } from "react";
import { BookText, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { PageShell } from "./page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import { FieldRow } from "./courses";
import { KnowledgeUploadButton } from "@/components/common/knowledge-upload-button";
import {
  useDeleteKnowledge,
  useKnowledge,
  useReindexKnowledge,
  useSaveKnowledge,
} from "@/lib/queries";
import { useToast } from "@/components/ui/toast";
import { formatRelative } from "@/lib/utils";
import type { KnowledgeDoc } from "@/lib/types";

export function KnowledgePage() {
  const { data, isLoading, isError, refetch } = useKnowledge();
  const reindex = useReindexKnowledge();
  const toast = useToast();
  const [editing, setEditing] = useState<KnowledgeDoc | "new" | null>(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        (d.category || "").toLowerCase().includes(q) ||
        (d.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [data, query]);

  const onReindex = async () => {
    try {
      const result = await reindex.mutateAsync();
      toast.success("Reindex complete", result?.reindexed ? `${result.reindexed} docs` : undefined);
    } catch (err) {
      toast.error("Reindex failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <PageShell
      title="Knowledge"
      description="Documents the bot can search to answer FAQ-style questions."
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={onReindex} disabled={reindex.isPending}>
            <RefreshCw className={`h-4 w-4 ${reindex.isPending ? "animate-spin" : ""}`} />
            Reindex
          </Button>
          <KnowledgeUploadButton />
          <Button variant="primary" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            New document
          </Button>
        </div>
      }
    >
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by title, category, tag..."
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState title="Couldn't load knowledge" onRetry={() => void refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={BookText}
          title={query ? "No matches" : "No documents yet"}
          description={query ? "Try a different query." : "Add the first document so the bot has something to cite."}
        />
      ) : (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {filtered.map((doc) => (
            <li
              key={doc.id}
              className="cursor-pointer px-5 py-4 transition-colors hover:bg-surface-muted"
              onClick={() => setEditing(doc)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-ink">{doc.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {doc.category && <Badge tone="outline">{doc.category}</Badge>}
                    {(doc.tags || []).map((tag) => (
                      <Badge key={tag} tone="neutral">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-ink-muted">{doc.content}</p>
                </div>
                <span className="text-xs text-ink-faint">{formatRelative(doc.updated_at)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <KnowledgeDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </PageShell>
  );
}

function KnowledgeDialog({ initial, onClose }: { initial: KnowledgeDoc | null; onClose: () => void }) {
  const save = useSaveKnowledge();
  const del = useDeleteKnowledge();
  const toast = useToast();
  const [form, setForm] = useState<Partial<KnowledgeDoc>>(
    initial
      ? { ...initial, tags: initial.tags ?? [] }
      : { title: "", category: "faq", tags: [], content: "" }
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync({
        ...form,
        ...(initial ? { id: initial.id } : {}),
        tags: typeof form.tags === "string"
          ? (form.tags as unknown as string).split(",").map((s) => s.trim()).filter(Boolean)
          : (form.tags ?? []),
      });
      toast.success(initial ? "Document updated" : "Document created");
      onClose();
    } catch (err) {
      toast.error("Save failed", err instanceof Error ? err.message : undefined);
    }
  };

  const onDelete = async () => {
    if (!initial) return;
    if (!confirm("Delete this document?")) return;
    try {
      await del.mutateAsync(initial.id);
      toast.success("Document deleted");
      onClose();
    } catch (err) {
      toast.error("Delete failed", err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit document" : "New document"}</DialogTitle>
          <DialogDescription>Markdown is supported. The bot will use this to answer factual questions.</DialogDescription>
        </DialogHeader>

        <form id="knowledge-form" onSubmit={onSubmit} className="space-y-3">
          <FieldRow label="Title" id="title">
            <Input
              id="title"
              value={form.title ?? ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </FieldRow>
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldRow label="Category" id="category">
              <Input
                id="category"
                value={form.category ?? ""}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Tags" id="tags" hint="Comma-separated">
              <Input
                id="tags"
                value={Array.isArray(form.tags) ? form.tags.join(", ") : (form.tags ?? "")}
                onChange={(e) => setForm({ ...form, tags: e.target.value as unknown as string[] })}
                placeholder="faq, pricing"
              />
            </FieldRow>
          </div>
          <FieldRow label="Content" id="content">
            <Textarea
              id="content"
              rows={12}
              value={form.content ?? ""}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="font-mono text-sm"
              required
            />
          </FieldRow>
        </form>

        <DialogFooter>
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              disabled={del.isPending}
              className="mr-auto"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="knowledge-form" variant="primary" disabled={save.isPending}>
            Save document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
